import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AdminUser, getErrorMessage, MIN_PASSWORD_LENGTH, userService, User } from '../services/api';
import AppNavBar from '../components/AppNavBar';
import PlansAdmin from '../components/PlansAdmin';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import {
    ChevronDown,
    ChevronUp,
    ChevronsUpDown,
    Users,
    Library,
    Search,
    Trash2,
    Pencil,
    Shield,
    UserPlus,
    Ban,
    CircleCheck,
    Download,
    MailCheck
} from 'lucide-react';
import { EmptyState, LoadingState, Modal, PageHeader } from '../components/ui';
import ContentLibrary from '../components/ContentLibrary';
import { dataExportFilename, saveFile } from '../utils/download';
import { formatMinutes, formatRelativeTime, formatUtc } from '../utils/format';

const USERS_PAGE_SIZE = 50;

// The list's orders (ACC-4): as the API returns it (oldest account first), or by a column, either way.
type SortKey = 'default' | 'last_login' | 'joined' | 'used_today';
type SortDir = 'asc' | 'desc';
const SORT_OPTIONS: { value: string; label: string }[] = [
    { value: 'default', label: 'Default order' },
    { value: 'last_login:desc', label: 'Last login, newest first' },
    { value: 'last_login:asc', label: 'Last login, oldest first' },
    { value: 'joined:desc', label: 'Joined, newest first' },
    { value: 'joined:asc', label: 'Joined, oldest first' },
    { value: 'used_today:desc', label: 'Time used today, most first' },
    { value: 'used_today:asc', label: 'Time used today, least first' },
];

// Simulation time used today: the user's own plus their class time (PLAN-3), for sorting.
const usedToday = (u: AdminUser) => u.time_used_today_s + u.class_time.reduce((sum, c) => sum + c.used_s, 0);

// The value a column sorts by; null (never logged in) always goes last.
const sortValue = (u: AdminUser, key: SortKey): number | null => {
    if (key === 'last_login') return u.last_login_at ? new Date(u.last_login_at).getTime() : null;
    if (key === 'joined') return new Date(u.created_at).getTime();
    if (key === 'used_today') return usedToday(u);
    return u.id;
};

const sortUsers = (list: AdminUser[], key: SortKey, dir: SortDir): AdminUser[] => {
    if (key === 'default') return list;
    return [...list].sort((a, b) => {
        const x = sortValue(a, key);
        const y = sortValue(b, key);
        if (x === null || y === null) return x === y ? a.id - b.id : x === null ? 1 : -1;
        return (dir === 'asc' ? x - y : y - x) || a.id - b.id;
    });
};

// "10 min", "1 h": a daily limit.
const perDay = (seconds: number) => (seconds >= 3600 && seconds % 3600 === 0 ? `${seconds / 3600} h` : formatMinutes(seconds));

const AdminDashboard: React.FC = () => {
    const { user } = useAuth();
    const toast = useToast();
    const confirm = useConfirm();
    const [activeTab, setActiveTab] = useState<'accounts' | 'lessons'>('accounts');
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(false);
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [showEditUserModal, setShowEditUserModal] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [search, setSearch] = useState('');
    const [showAllUsers, setShowAllUsers] = useState(false);
    const [sortKey, setSortKey] = useState<SortKey>('default');
    const [sortDir, setSortDir] = useState<SortDir>('desc');

    const [userData, setUserData] = useState({
        email: '',
        full_name: '',
        password: '',
        role: 'teacher' as 'teacher' | 'student' | 'admin'
    });

    const loadUsers = useCallback(async () => {
        setLoading(true);
        try {
            const data = await userService.getUsers();
            setUsers(data);
        } catch (error) {
            console.error('Error loading users:', error);
            toast.error(`Users could not be loaded: ${getErrorMessage(error)}`);
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        if (activeTab === 'accounts') {
            loadUsers();
        }
    }, [activeTab, loadUsers]);

    const handleCreateUser = async () => {
        if (userData.password.length < MIN_PASSWORD_LENGTH) {
            toast.error(`The password must be at least ${MIN_PASSWORD_LENGTH} characters long.`);
            return;
        }
        try {
            await userService.createUser(userData);
            setShowAddUserModal(false);
            setUserData({ email: '', full_name: '', password: '', role: 'teacher' });
            loadUsers();
            toast.success('User created successfully');
        } catch (error) {
            toast.error(`Error creating user: ${getErrorMessage(error, 'Unknown error')}`);
        }
    };

    const handleUpdateUser = async () => {
        if (!editingUser) return;
        try {
            // Don't send empty password
            const updatePayload = { ...userData };
            if (!updatePayload.password) delete (updatePayload as any).password;
            else if (updatePayload.password.length < MIN_PASSWORD_LENGTH) {
                toast.error(`The password must be at least ${MIN_PASSWORD_LENGTH} characters long.`);
                return;
            }

            await userService.updateUser(editingUser.id, updatePayload);
            setShowEditUserModal(false);
            setEditingUser(null);
            setUserData({ email: '', full_name: '', password: '', role: 'teacher' });
            loadUsers();
            toast.success('User updated successfully');
        } catch (error) {
            toast.error(`Error updating user: ${getErrorMessage(error, 'Unknown error')}`);
        }
    };

    const handleDeleteUser = async (userId: number, name: string) => {
        const confirmed = await confirm({
            title: `Delete user "${name}"?`,
            message: 'Their account and work are removed. This cannot be undone.',
            confirmLabel: 'Delete user',
            danger: true,
        });
        if (!confirmed) return;
        try {
            await userService.deleteUser(userId);
            toast.success('User deleted');
            loadUsers();
        } catch (error) {
            toast.error(`Error deleting user: ${getErrorMessage(error, 'Unknown error')}`);
        }
    };

    // Disabled accounts can't log in, and their existing logins stop working (docs/REQUIREMENTS.md ACC-4). The API
    // refuses to disable yourself or the last admin, and says why.
    const handleSetActive = async (target: User, isActive: boolean) => {
        if (!isActive) {
            const confirmed = await confirm({
                title: `Disable "${target.full_name}"?`,
                message: 'They can no longer log in, and are logged out everywhere. Their account and work are kept; you can enable it again at any time.',
                confirmLabel: 'Disable account',
                danger: true,
            });
            if (!confirmed) return;
        }
        try {
            await userService.updateUser(target.id, { is_active: isActive });
            toast.success(isActive ? 'Account enabled' : 'Account disabled');
            loadUsers();
        } catch (error) {
            toast.error(`${isActive ? 'Error enabling' : 'Error disabling'} the account: ${getErrorMessage(error, 'Unknown error')}`);
        }
    };

    // ACC-16: a self-registered account whose confirmation email never arrived; an admin vouches for the address.
    const handleConfirmEmail = async (target: User) => {
        const confirmed = await confirm({
            title: `Confirm the email of "${target.full_name}"?`,
            message: `Only when you know ${target.email} is really theirs (e.g. they wrote from it). They can then log in.`,
            confirmLabel: 'Confirm email',
        });
        if (!confirmed) return;
        try {
            await userService.updateUser(target.id, { email_confirmed: true });
            toast.success('Email confirmed');
            loadUsers();
        } catch (error) {
            toast.error(`Error confirming the email: ${getErrorMessage(error, 'Unknown error')}`);
        }
    };

    // ACC-15: a user's data for a request that came by email (contact@); the same file as their own download.
    const handleExport = async (target: User) => {
        try {
            saveFile(await userService.exportUser(target.id), dataExportFilename(`-user-${target.id}`));
        } catch (error) {
            toast.error(`The data of "${target.full_name}" could not be exported: ${getErrorMessage(error, 'Unknown error')}`);
        }
    };

    // ACC-4, ACC-6: when the account joined and was last used, relative, with the exact UTC time on hover; "never" covers
    // logins from before they were recorded.
    const moment = (value: string | null | undefined, testId?: string) => value ? (
        <time dateTime={value} title={formatUtc(value)} data-testid={testId}>{formatRelativeTime(value)}</time>
    ) : (
        <span data-testid={testId}>never</span>
    );

    // Today's simulation time (UTC day) as the app counts it for the user (PLAN-1, PLAN-2, PLAN-14): their own, and a
    // note of the class time of their paid classes (PLAN-3).
    const ownTime = (u: AdminUser) => {
        if (u.limit_s === null || u.time_left_today_s === null) {
            return <span title={`Used ${formatMinutes(u.time_used_today_s)} today`}>Unlimited</span>;
        }
        const label = u.time_left_today_s <= 0 ? 'Used up' : `${formatMinutes(u.time_left_today_s)} left`;
        return (
            <span title={`Used ${formatMinutes(u.time_used_today_s)} of ${perDay(u.limit_s)} today (UTC)`}>
                <span className={u.time_left_today_s <= 0 ? 'text-amber-700 dark:text-amber-400 font-medium' : ''}>{label}</span>
                <span className="text-gray-500 dark:text-gray-400"> of {perDay(u.limit_s)}</span>
            </span>
        );
    };

    const classTimeLine = (c: AdminUser['class_time'][number]) => `${c.name}: ${c.left_s <= 0 ? 'used up' : `${formatMinutes(c.left_s)} left`} of ${perDay(c.limit_s)}`;

    const classTime = (u: AdminUser) => {
        if (u.class_time.length === 0) return null;
        if (u.class_time.length === 1) {
            const c = u.class_time[0];
            return <p className="text-xs text-gray-500 dark:text-gray-400" title={`Class time: ${classTimeLine(c)}`}>Class “{c.name}”: {c.left_s <= 0 ? 'used up' : `${formatMinutes(c.left_s)} left`}</p>;
        }
        return (
            <details className="text-xs text-gray-500 dark:text-gray-400">
                <summary className="cursor-pointer select-none">Class time in {u.class_time.length} classes</summary>
                <ul className="mt-1 space-y-0.5">
                    {u.class_time.map((c) => <li key={c.class_id}>{classTimeLine(c)}</li>)}
                </ul>
            </details>
        );
    };

    const sortBy = (key: SortKey) => {
        if (key === sortKey) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    const sortHeader = (key: SortKey, label: string) => {
        const active = sortKey === key;
        const Icon = !active ? ChevronsUpDown : sortDir === 'asc' ? ChevronUp : ChevronDown;
        return (
            <th aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" onClick={() => sortBy(key)} className="inline-flex items-center gap-1 font-semibold uppercase tracking-wider hover:text-gray-900 dark:hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 rounded">
                    {label}
                    <Icon className={`w-3.5 h-3.5 ${active ? '' : 'opacity-50'}`} aria-hidden="true" />
                </button>
            </th>
        );
    };

    const openEditModal = (user: User) => {
        setEditingUser(user);
        setUserData({
            email: user.email,
            full_name: user.full_name,
            password: '', // Don't show password
            role: user.role
        });
        setShowEditUserModal(true);
    };

    const closeUserModal = () => {
        setShowAddUserModal(false);
        setShowEditUserModal(false);
        setEditingUser(null);
        setUserData({ email: '', full_name: '', password: '', role: 'teacher' });
    };

    // The list grows with every test and trial account: search it, and show the first page until asked for all.
    const query = search.trim().toLowerCase();
    const matchingUsers = query
        ? users.filter((u) => `${u.full_name} ${u.email} ${u.role}${u.is_active ? '' : ' disabled'}${u.email_confirmed_at ? '' : ' unconfirmed'}`.toLowerCase().includes(query))
        : users;
    const sortedUsers = sortUsers(matchingUsers, sortKey, sortDir);
    const visibleUsers = query || showAllUsers ? sortedUsers : sortedUsers.slice(0, USERS_PAGE_SIZE);

    const roleBadge = (role: User['role']) => (
        <span className={`badge ${role === 'admin' ? 'badge-red' : role === 'teacher' ? 'badge-blue' : 'badge-green'}`}>
            {role}
        </span>
    );

    const disabledBadge = (u: User) => !u.is_active && (
        <span className="badge badge-gray" data-testid="account-disabled">disabled</span>
    );

    // ACC-16: registered, but the confirmation link hasn't been opened yet (can't log in).
    const unconfirmedBadge = (u: User) => !u.email_confirmed_at && (
        <span className="badge badge-amber" data-testid="account-unconfirmed" title="The email address isn't confirmed yet">unconfirmed</span>
    );

    const userActions = (u: User) => (
        <div className="flex justify-end gap-1">
            {!u.email_confirmed_at && (
                <button
                    onClick={() => handleConfirmEmail(u)}
                    className="btn btn-ghost btn-icon"
                    title="Confirm email"
                    aria-label={`Confirm the email of ${u.full_name}`}
                >
                    <MailCheck />
                </button>
            )}
            <button
                onClick={() => openEditModal(u)}
                className="btn btn-ghost btn-icon"
                title="Edit user"
                aria-label={`Edit ${u.full_name}`}
            >
                <Pencil />
            </button>
            <button
                onClick={() => handleExport(u)}
                className="btn btn-ghost btn-icon"
                title="Export data"
                aria-label={`Export the data of ${u.full_name}`}
            >
                <Download />
            </button>
            {u.id !== user?.id && (u.is_active ? (
                <button
                    onClick={() => handleSetActive(u, false)}
                    className="btn btn-ghost btn-icon"
                    title="Disable account"
                    aria-label={`Disable ${u.full_name}`}
                >
                    <Ban />
                </button>
            ) : (
                <button
                    onClick={() => handleSetActive(u, true)}
                    className="btn btn-ghost btn-icon"
                    title="Enable account"
                    aria-label={`Enable ${u.full_name}`}
                >
                    <CircleCheck />
                </button>
            ))}
            {u.id !== user?.id && (
                <button
                    onClick={() => handleDeleteUser(u.id, u.full_name)}
                    className="btn btn-ghost-danger btn-icon"
                    title="Delete user"
                    aria-label={`Delete ${u.full_name}`}
                >
                    <Trash2 />
                </button>
            )}
        </div>
    );

    return (
        <div className="page-shell">
            <AppNavBar
                title="Admin Console"
                icon={<Shield />}
                roleLabel="System Administrator"
            />

            {/* Main Content */}
            <div className="page-container">
                {/* Navigation Tabs */}
                <div className="tabs mb-6 sm:mb-8">
                    <button onClick={() => setActiveTab('accounts')} className={`tab ${activeTab === 'accounts' ? 'tab-active' : ''}`} aria-pressed={activeTab === 'accounts'}>
                        <Users />
                        Accounts
                    </button>
                    <button onClick={() => setActiveTab('lessons')} className={`tab ${activeTab === 'lessons' ? 'tab-active' : ''}`} aria-pressed={activeTab === 'lessons'}>
                        <Library />
                        Content Library
                    </button>
                </div>

                {activeTab === 'accounts' ? (
                    <div className="space-y-10">
                        <section>
                            <PageHeader
                                title="Account Management"
                                subtitle={`${users.length} accounts`}
                                actions={
                                    <button onClick={() => setShowAddUserModal(true)} className="btn btn-primary btn-lg">
                                        <UserPlus />
                                        <span>Add New User</span>
                                    </button>
                                }
                            />

                            <div className="mb-4 flex flex-col gap-2 sm:flex-row md:block">
                                <div className="relative max-w-md flex-1">
                                    <label htmlFor="account-search" className="sr-only">Search accounts</label>
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    <input
                                        id="account-search"
                                        type="search"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Search by name, email or role"
                                        className="input pl-9"
                                    />
                                </div>
                                {/* Phones sort with this; the table's column headers do it from md up. */}
                                <div className="relative md:hidden sm:w-64">
                                    <label htmlFor="account-sort" className="sr-only">Sort accounts</label>
                                    <select
                                        id="account-sort"
                                        value={sortKey === 'default' ? 'default' : `${sortKey}:${sortDir}`}
                                        onChange={(e) => {
                                            const [key, dir] = e.target.value.split(':');
                                            setSortKey(key as SortKey);
                                            if (dir) setSortDir(dir as SortDir);
                                        }}
                                        className="select"
                                    >
                                        {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                </div>
                            </div>

                            {loading && users.length === 0 ? (
                                <LoadingState label="Loading users..." />
                            ) : matchingUsers.length === 0 ? (
                                <EmptyState icon={<Search />} title={query ? 'No accounts match your search.' : 'No accounts yet.'} />
                            ) : (
                                <div className="card overflow-hidden">
                                    {/* Cards on phones, a table from md up */}
                                    <ul className="md:hidden divide-y divide-gray-100 dark:divide-gray-800">
                                        {visibleUsers.map((u) => (
                                            <li key={u.id} className={`px-4 py-3 ${u.is_active ? '' : 'opacity-70'}`}>
                                                <div className="flex items-center gap-3">
                                                    <div className="min-w-0 flex-1">
                                                        {/* Badges wrap under a long name instead of squeezing it. */}
                                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                                                            <p className="font-medium text-gray-900 dark:text-white truncate max-w-full">{u.full_name}</p>
                                                            {roleBadge(u.role)}
                                                            {disabledBadge(u)}
                                                            {unconfirmedBadge(u)}
                                                        </div>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{u.email}</p>
                                                    </div>
                                                    {userActions(u)}
                                                </div>
                                                {/* The dates and today's time use the card's full width, under the actions. */}
                                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Joined {moment(u.created_at)} · Last login {moment(u.last_login_at)}</p>
                                                <p className="text-xs text-gray-700 dark:text-gray-300" data-testid="account-time-today">Today: {ownTime(u)}</p>
                                                {classTime(u)}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="hidden md:block overflow-x-auto">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>Full Name</th>
                                                    <th>Email</th>
                                                    <th>Role</th>
                                                    {sortHeader('joined', 'Joined')}
                                                    {sortHeader('last_login', 'Last login')}
                                                    {sortHeader('used_today', 'Time today')}
                                                    <th className="text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {visibleUsers.map((u) => (
                                                    <tr key={u.id} className={u.is_active ? '' : 'opacity-70'}>
                                                        <td className="font-medium !text-gray-900 dark:!text-white">{u.full_name}</td>
                                                        <td className="!text-gray-500 dark:!text-gray-400 max-w-[16rem] truncate" title={u.email}>{u.email}</td>
                                                        <td><div className="flex flex-wrap items-center gap-1.5">{roleBadge(u.role)}{disabledBadge(u)}{unconfirmedBadge(u)}</div></td>
                                                        <td className="!text-gray-500 dark:!text-gray-400 whitespace-nowrap">
                                                            {moment(u.created_at)}
                                                        </td>
                                                        <td className="!text-gray-500 dark:!text-gray-400 whitespace-nowrap">
                                                            {moment(u.last_login_at, 'account-last-login')}
                                                        </td>
                                                        <td className="tabular-nums min-w-[10rem] max-w-[15rem]" data-testid="account-time-today">
                                                            {ownTime(u)}
                                                            {classTime(u)}
                                                        </td>
                                                        <td className="!py-1.5">{userActions(u)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {visibleUsers.length < matchingUsers.length && (
                                        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 dark:border-gray-800 px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                            <span>Showing {visibleUsers.length} of {matchingUsers.length}</span>
                                            <button onClick={() => setShowAllUsers(true)} className="btn btn-secondary btn-sm">Show All</button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                        <PlansAdmin users={query ? matchingUsers : users} onChange={loadUsers} />
                    </div>
                ) : (
                    <ContentLibrary />
                )}
            </div>

            {/* Add/Edit User Modal */}
            {(showAddUserModal || showEditUserModal) && (
                <Modal
                    title={showAddUserModal ? 'Add New User' : 'Edit User'}
                    onClose={closeUserModal}
                    footer={
                        <>
                            <button onClick={closeUserModal} className="btn btn-secondary">
                                Cancel
                            </button>
                            <button onClick={showAddUserModal ? handleCreateUser : handleUpdateUser} className="btn btn-primary">
                                {showAddUserModal ? 'Create Account' : 'Save Changes'}
                            </button>
                        </>
                    }
                >
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="user-full-name" className="label">Full Name</label>
                            <input
                                id="user-full-name"
                                type="text"
                                value={userData.full_name}
                                onChange={(e) => setUserData({ ...userData, full_name: e.target.value })}
                                className="input"
                            />
                        </div>
                        <div>
                            <label htmlFor="user-email" className="label">Email Address</label>
                            <input
                                id="user-email"
                                type="email"
                                value={userData.email}
                                onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                                className="input"
                            />
                        </div>
                        <div>
                            <label htmlFor="user-password" className="label">
                                {showEditUserModal ? 'New Password (leave empty to keep current)' : 'Password'}
                            </label>
                            <input
                                id="user-password"
                                type="password"
                                value={userData.password}
                                onChange={(e) => setUserData({ ...userData, password: e.target.value })}
                                className="input"
                                autoComplete="new-password"
                            />
                            <p className="hint">At least {MIN_PASSWORD_LENGTH} characters.</p>
                        </div>
                        <div>
                            <label htmlFor="user-role" className="label">Role</label>
                            <div className="relative">
                                <select
                                    id="user-role"
                                    value={userData.role}
                                    onChange={(e) => setUserData({ ...userData, role: e.target.value as any })}
                                    className="select"
                                >
                                    <option value="student">Student</option>
                                    <option value="teacher">Professor</option>
                                    <option value="admin">Administrator</option>
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default AdminDashboard;
